import { ARCHETYPE_LABEL, AUTONOMY_LABEL, COVERAGE_STATES, type AgentDetail, type CoverageState, type Scenario } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Boxes, Cpu, Globe, Plug, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { ImpactList } from "@/components/domain/misc";
import { PillarBars, ScoreGauge } from "@/components/domain/score";
import { StatTile } from "@/components/domain/stat";
import { CoverageChip } from "@/components/domain/status";
import { QueryState } from "@/components/layout/page";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState, Meter, Skeleton } from "@/components/ui/misc";
import { AgentIssuesTable, isOpenIssue, sortIssues } from "@/features/agents/agent-issues-table";
import { AUDIENCE_LABEL, ControlAdoptionInfo, MAGNITUDE_COLOR, MetaRow, Tag } from "@/features/agents/bits";
import { ScoreDrivers } from "@/features/agents/score-drivers";
import { PolicyChip, policyColors } from "@/features/coverage/policy-bits";
import { q } from "@/lib/api";
import { fmtInt, fmtMoney, fmtPct } from "@/lib/format";
import { usePackName } from "@/lib/packs";

export const Route = createFileRoute("/_app/agents/$agentId/")({
  component: AgentSummaryTab,
});

const DEP_ICON: Record<AgentDetail["dependencies"][number]["kind"], ReactNode> = {
  model: <Cpu />,
  mcp_server: <Plug />,
  api: <Globe />,
  framework: <Boxes />,
};
const DEP_LABEL: Record<AgentDetail["dependencies"][number]["kind"], string> = { model: "Model", mcp_server: "MCP server", api: "API", framework: "Framework" };

function AgentSummaryTab() {
  const { agentId } = Route.useParams();
  const agent = useQuery(q.agent(agentId));
  return (
    <QueryState query={agent} loading={<SummarySkeleton />}>
      {(a) => <Summary agent={a} />}
    </QueryState>
  );
}

function SummarySkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-12">
      <Skeleton className="h-80 lg:col-span-4" />
      <Skeleton className="h-80 lg:col-span-8" />
      <Skeleton className="h-64 lg:col-span-12" />
    </div>
  );
}

function Summary({ agent: a }: { agent: AgentDetail }) {
  const issues = useQuery(q.issues({ agentId: a.id }));
  const scenarios = useQuery(q.scenarios({ agentId: a.id }));
  const packName = usePackName();
  const activity = useQuery(q.activityStats({ agentId: a.id }));
  const policies = useQuery(q.policies());
  const colors = policyColors(policies.data);
  const open = issues.data ? sortIssues(issues.data.items.filter(isOpenIssue)) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-4">
          <CardHeader title="Agent Reliability Score" description="0–1000 · rolling 30 days · exposure-weighted" />
          <CardBody className="flex flex-col gap-5">
            <div className="flex justify-center pb-6 pt-1">
              <ScoreGauge value={a.ars} tier={a.tier} delta={a.arsDelta30d} />
            </div>
            <PillarBars pillars={a.pillars} />
          </CardBody>
        </Card>
        <Card className="flex flex-col lg:col-span-8">
          <CardHeader
            title="Top score drivers"
            description="What moves this agent's score, and what fixing it is worth."
            actions={
              <Link to="/agents/$agentId/score" params={{ agentId: a.id }} className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
                Score history <ArrowRight className="size-3" />
              </Link>
            }
          />
          <ScoreDrivers agentId={a.id} ars={a.ars} factors={a.topFactors} limit={6} />
        </Card>
      </div>

      <section aria-label="Key stats" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatTile label="Sessions / 24h" value={fmtInt(a.sessions24h)} sub={`${a.environment} · version ${a.currentVersion}`} />
        <StatTile
          label="Out of policy · 7d"
          href="/activity"
          search={{ agent: a.id, authority: "outside" }}
          value={activity.data ? fmtInt(activity.data.outOfPolicy7d) : "—"}
          sub={
            activity.data?.outOfPolicyImpact7d.length ? (
              <ImpactList impact={activity.data.outOfPolicyImpact7d} compact max={2} />
            ) : (
              `of ${fmtInt(activity.data?.total7d ?? 0)} actions & statements`
            )
          }
        />
        <StatTile
          label="Control adoption"
          icon={<ControlAdoptionInfo />}
          value={fmtPct(a.controlAdoption)}
          sub="high-risk actions under a platform control"
          footer={<Meter value={a.controlAdoption} tone={MAGNITUDE_COLOR} label="Control adoption" />}
        />
        <StatTile
          label="Telemetry completeness"
          value={`${(a.telemetryCompleteness * 100).toFixed(1)}%`}
          sub="side-effecting actions traced"
          footer={<Meter value={a.telemetryCompleteness} tone={MAGNITUDE_COLOR} label="Telemetry completeness" />}
        />
        <StatTile
          label="Credibility"
          value={
            <>
              {fmtPct(a.credibility)} <span className="text-sm font-normal text-fg-3">own-data</span>
            </>
          }
          sub={a.credibility >= 1 ? "fully credible" : `${fmtPct(1 - a.credibility)} from segment prior`}
          footer={<Meter value={a.credibility} tone={MAGNITUDE_COLOR} label="Credibility (share of score from own data)" />}
        />
      </section>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-8">
          <CardHeader
            title="Open issues"
            description="Unresolved issues for this agent, most urgent first."
            actions={
              <Link to="/issues" className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
                All issues <ArrowRight className="size-3" />
              </Link>
            }
          />
          <CardBody className="p-0">
            {issues.error ? (
              <div className="p-4">
                <QueryState query={issues}>{() => null}</QueryState>
              </div>
            ) : (
              <AgentIssuesTable issues={open} loading={issues.isPending} />
            )}
          </CardBody>
        </Card>

        <div className="flex flex-col gap-4 lg:col-span-4">
          <Card>
            <CardHeader
              title="Coverage"
              description={
                a.policyIds.length
                  ? `Insured limit ${fmtMoney(a.insuredLimit)} across ${a.policyIds.length} ${a.policyIds.length === 1 ? "policy" : "policies"}`
                  : "No policy covers this agent yet"
              }
              actions={
                <Link to="/agents/$agentId/coverage" params={{ agentId: a.id }} className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
                  Details <ArrowRight className="size-3" />
                </Link>
              }
            />
            <CardBody className="flex flex-col gap-2 py-2">
              {a.policyIds.length ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {a.policyIds.map((id) => (
                    <PolicyChip key={id} id={id} name={policies.data?.find((p) => p.id === id)?.name ?? id} color={colors.get(id)} />
                  ))}
                </div>
              ) : null}
              <QueryState query={scenarios} loading={<Skeleton className="h-24" />}>
                {(s) => <ScenarioStateSummary scenarios={s} />}
              </QueryState>
              <Link
                to="/coverage/quote"
                search={{ agents: a.id }}
                className="mb-1 inline-flex items-center gap-1 self-start text-xs font-medium text-accent hover:underline"
              >
                <Plus className="size-3" /> Insure more scenarios
              </Link>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Profile"
              actions={
                <Link to="/agents/$agentId/profile" params={{ agentId: a.id }} className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
                  Risk profile <ArrowRight className="size-3" />
                </Link>
              }
            />
            <CardBody className="divide-y py-1">
              <MetaRow label="Autonomy">{AUTONOMY_LABEL[a.autonomy]}</MetaRow>
              <MetaRow label="Archetype">{ARCHETYPE_LABEL[a.archetype]}</MetaRow>
              <MetaRow label="Audience">{AUDIENCE_LABEL[a.audience]}</MetaRow>
              <MetaRow label="Active packs">
                <span className="flex flex-wrap justify-end gap-1">
                  {a.activePacks.map((p) => (
                    <Tag key={p}>{packName(p)}</Tag>
                  ))}
                </span>
              </MetaRow>
              <MetaRow label="Dependencies">
                <span className="flex flex-wrap justify-end gap-1">
                  {a.dependencies.length ? (
                    a.dependencies.map((d) => (
                      <Tag key={`${d.kind}:${d.name}`} mono>
                        <span title={DEP_LABEL[d.kind]} aria-label={DEP_LABEL[d.kind]} className="inline-flex">
                          {DEP_ICON[d.kind]}
                        </span>
                        {d.name}
                      </Tag>
                    ))
                  ) : (
                    <span className="text-fg-3">None detected</span>
                  )}
                </span>
              </MetaRow>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ScenarioStateSummary({ scenarios }: { scenarios: Scenario[] }) {
  if (!scenarios.length) return <EmptyState className="py-6" title="No scenarios yet" description="Scenarios are registered once action classes are observed and confirmed." />;
  const byState = new Map<CoverageState, Scenario[]>();
  for (const s of scenarios) byState.set(s.state, [...(byState.get(s.state) ?? []), s]);
  return (
    <ul className="flex flex-col divide-y">
      {COVERAGE_STATES.filter((st) => byState.has(st)).map((st) => {
        const items = byState.get(st)!;
        const limit = items.reduce((n, s) => n + s.subLimit.amount, 0);
        return (
          <li key={st} className="flex items-center gap-3 py-2">
            <CoverageChip state={st} />
            <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-fg-3" title={items.map((s) => s.key).join(", ")}>
              {items.map((s) => s.key).join(", ")}
            </span>
            <span className="shrink-0 text-xs tabular text-fg-2">
              {items.length} · {fmtMoney(limit, { compact: true })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
